import React, { Component, Fragment, Children } from 'react';
import Siema from '../../vendor/siema';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Dot from './Dot';
import { generateKey } from '../../utils';

const Wrapper = styled.div`
  position: relative;

  ${({ siemaClassName }) =>
    siemaClassName &&
    `
    .${siemaClassName} > div {
      overflow: hidden;
    }
  `};
`;

const Dots = styled.ul`
  position: absolute;
  bottom: -57px;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  padding: 0;
  white-space: nowrap;
`;

const DotListEntry = styled.li`
  display: inline-block;

  &:not(:last-child) {
    margin-right: 5px;
  }
`;

const ArrowLeft = styled.div`
  position: absolute;
  border-right: 4px solid ${({ color }) => color};
  border-bottom: 4px solid ${({ color }) => color};
  width: 20px;
  height: 20px;
  transform: rotate(-225deg);
  left: 40px;
  top: 50%;
  z-index: 200;
  cursor: pointer;
`;

const ArrowRight = styled.div`
  position: absolute;
  border-right: 4px solid ${({ color }) => color};
  border-bottom: 4px solid ${({ color }) => color};
  width: 20px;
  height: 20px;
  transform: rotate(-45deg);
  right: 40px;
  top: 50%;
  z-index: 200;
  cursor: pointer;
`;

// needed for hair diagnosis to have slides with different height
/* eslint-disable */
Siema.prototype.setAutoHeight = function(stopTime) {
  let that, timeout;

  that = this;

  function autoHeight() {
    let currentItems, min, max, itemHeightList, height, maxHeight, i;

    min = that.currentSlide;
    max = min + that.perPage;
    itemHeightList = [];

    for (i = min; i < max; i++) {
      height = parseInt(that.innerElements[i].scrollHeight, 10);
      itemHeightList.push(height);
    }

    maxHeight = Math.max.apply(null, itemHeightList);

    that.sliderFrame.style.maxHeight = `${maxHeight}px`;
  }

  window.addEventListener('resize', () => {
    that.sliderFrame.style.maxHeight = '';
    clearTimeout(timeout);
    timeout = setTimeout(autoHeight, 500);
  });
  autoHeight();
};
/* eslint-enable */

/**
 * スライダー
 *   * 丸ポチのインクリメント毎にページ送りする場合に使用する
 *   * 使用ルール:
 *       1. アイテム総数は、丸ポチの倍数であること
 *       2. 丸ポチ数は、アイテム総数の約数であること
 */
class Slider extends Component {
  constructor(props) {
    super(props);

    // each siema carousel needs to have a unique class otherwise they don't initialize
    const randomSiemaClass = `siema-${Math.floor(Math.random() * 100000)}`;

    this.state = {
      currentSlide: this.props.currentSlide,
      className: randomSiemaClass,
    };
  }

  componentDidMount() {
    const { options, autoHeight } = this.props;
    const { className } = this.state;

    this.siema = new Siema({
      ...options,
      selector: `.${className}`,
      onChange: this.handleOnChange,
    });

    if (autoHeight) this.siema.setAutoHeight();
    this.startInterval();

    this.setState({ currentSlide: this.siema.currentSlide }); // eslint-disable-line
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.currentSlide !== this.props.currentSlide) {
      if (this.siema.currentSlide !== nextProps.currentSlide) {
        this.handleSlideChange(nextProps.currentSlide);
      } else {
        this.setState({ currentSlide: nextProps.currentSlide });
      }
    }

    // reinitialize slider with different options
    if (JSON.stringify(nextProps.options) !== JSON.stringify(this.props.options)) {
      this.siema.destroy(true, () => {
        this.siema = new Siema({
          ...nextProps.options,
          selector: `.${this.state.className}`,
          onChange: () => this.handleOnChange(nextProps),
        });
        if (nextProps.autoHeight) {
          this.siema.setAutoHeight();
        }
      });
    }

    // if children elements have changed, revert the DOM
    // and rebuild Siema after React has updated the DOM
    // in componentDidUpdate
    // otherwise, React will try to update children which are not there
    // because Siema mutates the DOM
    if (
      JSON.stringify(Children.map(nextProps.children, child => child.key)) !==
      JSON.stringify(Children.map(this.props.children, child => child.key))
    ) {
      this.siema.destroy(true);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(Children.map(prevProps.children, child => child.key)) !==
      JSON.stringify(Children.map(this.props.children, child => child.key))
    ) {
      this.siema = new Siema({
        ...this.props.options,
        selector: `.${this.state.className}`,
        onChange: this.handleOnChange,
      });
      if (this.props.autoHeight) {
        this.siema.setAutoHeight();
      }
    }
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  handleOnChange = (props = this.props) => {
    this.updateCurrentSlide(this.siema.currentSlide);
    if (props.options.onChange) props.options.onChange(this.siema.currentSlide);
    if (props.autoHeight) this.siema.setAutoHeight();
    this.resetInterval();
  };

  handleSlideChange = i => {
    const { currentSlide } = this.state;
    const { children } = this.props;
    const totalSlides = Children.count(children);

    if (i !== currentSlide) {
      if ((i === 0 && currentSlide === totalSlides) || i === currentSlide + 1) {
        this.setState({ currentSlide: i }, () => {
          this.siema.next();
        });
      } else if ((i === totalSlides && currentSlide === 0) || i === currentSlide - 1) {
        this.setState({ currentSlide: i }, () => {
          this.siema.prev();
        });
      } else {
        this.setState({ currentSlide: i }, () => {
          this.siema.goTo(i);
        });
      }
    }
  };

  handleClickPrev = () => {
    if (this.props.currentSlide === undefined) {
      const { currentSlide } = this.state;
      const { children, options } = this.props;
      const { perPage = 1, loop } = options;

      if (currentSlide > 0) {
        this.setState({ currentSlide: currentSlide - 1 }, () => {
          this.siema.prev();
        });
      } else if (loop) {
        const totalSlides = Children.count(children);
        this.setState({ currentSlide: totalSlides - perPage }, () => {
          this.siema.prev();
        });
      }
    }
  };

  handleClickNext = () => {
    if (this.props.currentSlide === undefined) {
      const { currentSlide } = this.state;
      const { children, options } = this.props;
      const { perPage = 1, loop } = options;
      const totalSlides = Children.count(children);

      if (currentSlide < totalSlides - perPage) {
        this.setState({ currentSlide: currentSlide + 1 }, () => {
          this.siema.next();
        });
      } else if (loop) {
        this.setState({ currentSlide: 0 }, () => {
          this.siema.next();
        });
      }
    }
  };

  resetInterval = () => {
    if (this.interval) {
      clearInterval(this.interval);
      this.startInterval();
    }
  };

  startInterval = () => {
    if (this.props.auto) {
      this.interval = setInterval(() => {
        this.handleClickNext();
      }, this.props.auto);
    }
  };

  updateCurrentSlide = currentSlide => this.setState({ currentSlide });

  render() {
    const { currentSlide, className } = this.state;
    const {
      arrows,
      arrowLeft,
      arrowRight,
      autoHeight,
      children,
      dots,
      dotsStyle,
      dotsColor,
      keys,
      options,
    } = this.props;
    const childrenLength = Children.count(children);
    const { perPage = 1 } = options;
    const dotsNo = perPage ? childrenLength - perPage + 1 : childrenLength;
    let defaultArrowLeft = null;
    let defaultArrowRight = null;

    if ((!arrowLeft || !arrowRight) && (dotsNo > 1 && arrows)) {
      defaultArrowLeft = <ArrowLeft onClick={this.handleClickPrev} color={dotsColor} />;
      defaultArrowRight = <ArrowRight onClick={this.handleClickNext} color={dotsColor} />;
    }

    return (
      <Fragment>
        {dotsNo > 1 &&
          arrows &&
          arrowLeft &&
          React.cloneElement(arrowLeft, {
            onClick: () => {
              const { onClick } = arrowLeft.props;
              if (onClick) onClick();
              this.handleClickPrev();
            },
          })}

        {dotsNo > 1 &&
          arrows &&
          arrowRight &&
          React.cloneElement(arrowRight, {
            onClick: () => {
              const { onClick } = arrowRight.props;
              if (onClick) onClick();
              this.handleClickNext();
            },
          })}

        <Wrapper siemaClassName={autoHeight && className}>
          {defaultArrowLeft}
          <div className={className}>{children}</div>
          {defaultArrowRight}
          {dots &&
            dotsNo > 1 &&
            dotsNo < 10 && (
              <Dots style={dotsStyle}>
                {keys.slice(0, dotsNo).map((key, i) => (
                  <DotListEntry key={generateKey('slider', i)}>
                    <Dot onClick={this.handleSlideChange} index={i} current={currentSlide === i} color={dotsColor} />
                  </DotListEntry>
                ))}
              </Dots>
            )}
        </Wrapper>
      </Fragment>
    );
  }
}

Slider.defaultProps = {
  auto: 0,
  arrows: false,
  arrowLeft: null,
  arrowRight: null,
  dots: true,
  dotsColor: '#000',
  dotsStyle: {},
  currentSlide: undefined,
  keys: [''],
  options: {
    selector: `.siema`,
    duration: 200,
    easing: 'ease-out',
    perPage: 1,
    startIndex: 0,
    draggable: true,
    multipleDrag: true,
    threshold: 20,
    loop: false,
    rtl: false,
    onInit: () => {},
    onChange: () => {},
  },
  autoHeight: false,
};

Slider.propTypes = {
  auto: PropTypes.number,
  arrows: PropTypes.bool,
  arrowLeft: PropTypes.element,
  arrowRight: PropTypes.element,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  currentSlide: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  dots: PropTypes.bool,
  dotsColor: PropTypes.string,
  dotsStyle: PropTypes.object, // eslint-disable-line
  keys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  options: PropTypes.shape({
    selector: PropTypes.string,
    duration: PropTypes.number,
    easing: PropTypes.string,
    perPage: PropTypes.number,
    startIndex: PropTypes.number,
    draggable: PropTypes.bool,
    multipleDrag: PropTypes.bool,
    threshold: PropTypes.number,
    loop: PropTypes.bool,
    rtl: PropTypes.bool,
    onInit: PropTypes.func,
    onChange: PropTypes.func,
  }),
  autoHeight: PropTypes.bool,
};

export default Slider;



// WEBPACK FOOTER //
// ./src/components/Slider/Slider.js